import React, { useEffect, useState } from "react";
import { Box, Button, Tooltip } from "@mui/material";
import ProgressBarComponent from "./Components/ProgressBarComponent";
import { InfoOutlined } from "@mui/icons-material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import styled from "styled-components";
import JobCategoryCard from "./Components/JobCategoryCard";
import WeightInfoBox from "./Components/WeightInfoBox";
import ChatBoxComponent from "./Components/ChatBox";
import { GetJobBarDetails, updateJobBarDetails, PublishJob } from "./services";
import { IUpdateJobBarDetails } from "./types";
import { DEFAULT_TOKEN } from "utilities/constants";
import { toast } from "react-toastify";
import { useAddJobContext } from "./context/AddJobContext";

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 15px 20px;
  margin-top: 15px;
  margin-bottom: 10px;
`;

const Title = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: #333333;
  margin: 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LinkButton = styled(Button)`
  text-transform: none;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  box-shadow: none;

  &:hover {
    background: #f0f4ff;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  transition: all 0.3s ease;
`;

const MainContent = styled.div<{ isInfoPanelOpen: boolean; isChatBoxOpen: boolean; }>`
  flex: ${(props) =>
    props.isInfoPanelOpen || props.isChatBoxOpen ? "0 0 65%" : "0 0 100%"};
  transition: all 0.3s ease;
  padding-right: ${(props) =>
    props.isInfoPanelOpen || props.isChatBoxOpen ? "20px" : "0"};
`;

const InfoPanelContainer = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 35%;
  height: 50%;
  z-index: 5;
  display: ${(props) => (props.isVisible ? "block" : "none")};
`;

const ChatBoxContainer = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 35%;
  z-index: 5;
  display: ${(props) => (props.isVisible ? "block" : "none")};
`;

const ReviewJobBar: React.FC<{ submitBtnRef: any }> = ({ submitBtnRef }) => {
  const [categories, setCategories] = useState<any[]>([]);
  const [headerWeights, setHeaderWeights] = useState<number[]>([]);
  const [totalProgress, setTotalProgress] = useState<number>(0);
  const [showWeightError, setShowWeightError] = useState<boolean>(false);
  const [showWeightBox, setShowWeightBox] = useState<boolean>(false);
  const [showChatBox, setShowChatBox] = useState<boolean>(false);
  const { expertId, jobId, setLoading, handleSetJobId } = useAddJobContext();

  useEffect(() => {
    setLoading(true);
    const fetchJobBarDetails = async () => {
      try {
        const barPayload = {
          jobId,
          expertId,
          token: DEFAULT_TOKEN,
        };
        const response = await GetJobBarDetails(barPayload);

        if (response.apiStatus === "SUCCESS" && response.output?.jobBarCategories) {
          const fetched = response.output.jobBarCategories;
          setCategories(fetched);
          const weights = fetched.map((cat: any) => cat.requiredWeight);
          setHeaderWeights(weights);
          const total = weights.reduce((sum:number, w:number) => sum + w, 0);
          setTotalProgress(total);
          setShowWeightError(total > 100);
        }
      } catch (err) {
        console.error("Error fetching job bar details:", err);
        toast.error("Something went wrong while loading job bar details.");
      } finally {
        setLoading(false);
      }
    };
    if (jobId && expertId){
      fetchJobBarDetails();
    }
    
  }, [jobId, expertId]);

  const updateCategory = (
    name: string,
    newWeight: number,
    newValues: { name: string; difficultyLevel: string }[]
  ) => {
    const updated = categories.map((cat) =>
      cat.name === name ? { ...cat, requiredWeight: newWeight, values: newValues } : cat
    );
    setCategories(updated);

    const weights = updated.map((cat) => cat.requiredWeight);
    setHeaderWeights(weights);
    const sum = weights.reduce((a, b) => a + b, 0);
    setTotalProgress(sum);
    setShowWeightError(sum > 100);
  };

  const handleSaveJobBar = async () => {
    setLoading(true);

    const cleaned = categories.map(({ name, requiredWeight, values }) => ({
      name,
      requiredWeight,
      values: values.filter((v:any) => v.name.trim() && v.difficultyLevel.trim()),
    }));

    const payload: IUpdateJobBarDetails = {
      expertId,
      jobId,
      jobBarCategories: cleaned,
    };

    try {
      const res = await updateJobBarDetails(payload);
      if (res.apiStatus === "SUCCESS") {
        try {
          await PublishJob({
            jobId, expertId, jobDraftState: "PUBLISHED",
          });
        } catch (publishErr) {
          console.error("Error publishing job:", publishErr);
          toast.error("Job saved, but error occurred while publishing.");
        }

        handleSetJobId(jobId, "thankyou");
      } else {
        toast.error("Something went wrong, please try again!");
      }
    } catch (err) {
      toast.error("Something went wrong, please try again!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ marginTop: "30px", width: "99%", px: 0, paddingBottom: "20px", position: "relative", overflow: "hidden" }}>
      <ContentContainer>
        <MainContent isInfoPanelOpen={showWeightBox} isChatBoxOpen={showChatBox}>
          <Box sx={{ marginLeft: "20px" }}>
            <ProgressBarComponent
              headerWeights={headerWeights}
              totalProgress={totalProgress}
              categoryNames={categories.map((cat) => cat.name)}
              showWeightError={showWeightError}
            />
          </Box>

          <HeaderWrapper>
            <Title>Job Bar categories</Title>
            {!showWeightBox && !showChatBox && (
              <ButtonContainer>
                <Tooltip title="Learn how weights affect candidate matching" arrow>
                  <LinkButton
                    sx={{ color: "#6D758D" }}
                    startIcon={<InfoOutlined fontSize="small" />}
                    onClick={() => setShowWeightBox(true)}
                  >
                    Learn how weights work?
                  </LinkButton>
                </Tooltip>
              </ButtonContainer>
            )}
          </HeaderWrapper>

          <Box sx={{ width: "99%", marginLeft: "20px" }}>
            {categories.map((category, index) => (
              <JobCategoryCard
                key={index}
                category={category}
                showWeightBox={showWeightBox}
                showChatBox={showChatBox}
                updateCategory={updateCategory}
              />
            ))}
          </Box>

          <Button onClick={handleSaveJobBar} ref={submitBtnRef} style={{ display: "none" }}>
            Save Job Bar
          </Button>
        </MainContent>

        <InfoPanelContainer isVisible={showWeightBox}>
          <WeightInfoBox onClose={() => setShowWeightBox(false)} />
        </InfoPanelContainer>

        <ChatBoxContainer isVisible={showChatBox}>
          <ChatBoxComponent onClose={() => setShowChatBox(false)} />
        </ChatBoxContainer>
      </ContentContainer>
    </Box>
  );
};

export default ReviewJobBar;